import React, { useState } from "react";
import "./Research.css";
import { Helmet } from "react-helmet";

const Research = () => {
  const [activeTab, setActiveTab] = useState("master");

  const researchTabs = {
    bachelor: [
      {
        title: "Gradient Descent with Random Minibatches",
        description: "This thesis explores the dynamics and convergence properties of gradient descent algorithms when integrated with random minibatches.",
        tags: ["Grade: 9/10", "Bachelor thesis"],
        supervisors: [
          { name: "Prof. Johannes Schmidt-Hieber", link: "https://jschmidthieber.personalweb.utwente.nl/#" },
          { name: "Dr. Gabriel Clara", link: "https://gclara.gitlab.io/" },
        ],
        paperLink: "https://essay.utwente.nl/100665/",
        codeLink: "https://github.com/yazanmashal03/projects/tree/main/bachelor_thesis/src",
        slidesLink: "",
      },
      {
        title: "Developing a Parity Game GUI",
        description: "Designing and developing a GUI to display and solve parity games.",
        tags: ["Grade: 8/10", "Design project"],
        paperLink: "https://bachelorshowcase-eemcs.apps.utwente.nl/view/4XMBs3wv",
        codeLink: "https://github.com/MikiVanousek/parity-games",
        slidesLink: "",
        supervisors: [{ name: "Prof. Tom van Dijk", link: "https://www.tvandijk.nl/" }],
      },
      {
        title: "Internship for imec Inc. in Leuven",
        description: "The internship project for imec revolved around designing and developing a stock management system for the nanotechnology company.",
        tags: ["Grade: 9/10", "Internship"],
        paperLink: "https://drive.google.com/file/d/1pxxkhMFYWGncaZM9RqVzO3sQVQDbM8vP/view",
        codeLink: "",
        slidesLink: "",
        supervisors: [{ name: "Alex van Oostrum", link: "https://www.linkedin.com/in/alex-van-oostrum-01860722/?originalSubdomain=nl" }],
      },
      {
        title: "Statistical Analysis of the Poverty Index",
        description: "Leveraged statistical tools to draw important conclusions from the Poverty Index provided by the United Nations Data website.",
        tags: ["Grade: 9.5/10", "Best grade", "R"],
        paperLink: "https://drive.google.com/file/d/1hdskYpqbOcW-UjXvUKOZeBsUe7nqk_XS/view",
        codeLink: "",
        slidesLink: "",
      },
      {
        title: "Modeling the Waters of Walsoorden",
        description: "Modeled the water levels of the Walsoorden area.",
        tags: ["Grade: 7.5/10", "Python"],
        paperLink: "https://drive.google.com/file/d/1TQw9C-T4BvVM6TWpMmMROBagTlJeUqRB/view",
        codeLink: "",
        slidesLink: "",
      },
    ],
    master: [
      {
        title: "Mean-Field Analysis of Dropout Neural Networks",
        description: "We study the empirical measure of the neuron parameters. Under suitable regularity and moment assumptions, we prove a law of large numbers, convergence of the fluctuations, and a central-limit theorem.",
        tags: ["Grade: 8/10", "Mean-Field", "Dropout Neural Networks", "Master thesis"],
        paperLink: "https://resolver.tudelft.nl/uuid:1a8dfbea-0496-40af-9ff9-8c68e4d0d35d",
        slidesLink: "https://drive.google.com/file/d/1Ts-jGCHxGwL-m2Uoq1eOIBb1j0eeoJWx/view?usp=sharing",
        supervisors: [
          { name: "Prof. Frank Redig", link: "https://diamhomes.ewi.tudelft.nl/~jredig/" },
          { name: "Dr. Anna Shalova", link: "https://sites.google.com/view/annashalova" },
        ],
      },
      {
        title: "NTK Analysis for Finite Width and Depth",
        description: "This paper explores the Neural Tangent Kernel for finite width and depth neural networks.",
        tags: ["Grade: 9/10", "Machine learning"],
        paperLink: "https://drive.google.com/file/d/19KqXkUTSxfgApSLAwLrBlYemgyrPeU9m/view?usp=sharing",
        codeLink: "https://github.com/yazanmashal03/ntk_analysis",
        slidesLink: "",
      },
      {
        title: "Characterising Toxicity in Generative Large Language Models",
        description: "This paper explores which lexical and syntactic structures are more likely to trigger toxic output from generative language models.",
        tags: ["Grade: 8/10", "NLP"],
        paperLink: "https://drive.google.com/file/d/1aD4txp7KNLjEfj0frm9kXpeo4sIBfiJt/view?usp=sharing",
        codeLink: "https://github.com/yazanmashal03/nlp-project",
        slidesLink: "",
      },
      {
        title: "Style Transfer for Image Processing",
        description: "A report detailing implementation choices, contributions, and results for an applied image processing project centered on style transfer.",
        tags: ["Grade: 9/10", "Computer vision", "Python"],
        paperLink: "https://drive.google.com/file/d/1c6DutNdOe7_ifEenEBA9bR5JigKbgQ_9/view?usp=sharing",
        codeLink: "https://drive.google.com/file/d/1GnIklHuFMKDmHaqtNKw9ipK36vkBU77t/view?usp=sharing",
        slidesLink: "",
      },
    ],
    pub: [
      {
        title: "The Interplay of Statistics and Noisy Optimization: Learning Linear Predictors with Random Data Weights",
        description: "We analyze gradient descent with randomly weighted data points in a linear regression model under a generic weighting distribution.",
        tags: ["Preprint", "StatML"],
        paperLink: "https://arxiv.org/abs/2512.10188",
        codeLink: "https://github.com/yazanmashal03/random-minibatches",
        slidesLink: "",
      },
    ],
  };

  const tabLabels = {
    bachelor: "Bachelor",
    master: "Master",
    pub: "Preprints",
  };

  return (
    <div className="research-container page-shell">
      <Helmet>
        <title>Research | Yazan Mash'Al</title>
      </Helmet>

      <section className="research-hero section-panel">
        <span className="eyebrow">Research</span>
        <h1 className="page-title">Papers, theses, and lines of inquiry.</h1>
        <p className="page-intro">
          My work sits around learning theory, stochastic analysis, and mathematically grounded
          machine learning. This page collects academic projects across bachelor, master, and preprint work.
        </p>
      </section>

      <div className="sub-tab-buttons" role="tablist" aria-label="Research categories">
        {Object.keys(researchTabs).map((tabKey) => (
          <button
            key={tabKey}
            className={activeTab === tabKey ? "active" : ""}
            onClick={() => setActiveTab(tabKey)}
            role="tab"
            aria-selected={activeTab === tabKey}
          >
            {tabLabels[tabKey] || tabKey}
          </button>
        ))}
      </div>

      <div className="sub-tab-content">
        {researchTabs[activeTab].map((researchItem) => (
          <article key={researchItem.title} className="research-item section-panel">
            <div className="research-item__header">
              <h3>{researchItem.title}</h3>
              <div className="research-tags">
                {researchItem.tags?.map((tag) => (
                  <span key={tag} className="research-tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <p>{researchItem.description}</p>

            {researchItem.supervisors?.length > 0 && (
              <p className="supervisors">
                <strong>Supervisors:</strong>{" "}
                {researchItem.supervisors.map((supervisor, index) => (
                  <span key={supervisor.link}>
                    <a href={supervisor.link} target="_blank" rel="noopener noreferrer" className="supervisor-link">
                      {supervisor.name}
                    </a>
                    {index < researchItem.supervisors.length - 1 ? ", " : ""}
                  </span>
                ))}
              </p>
            )}

            <div className="research-links">
              {researchItem.paperLink && (
                <a href={researchItem.paperLink} target="_blank" rel="noopener noreferrer">
                  View paper
                </a>
              )}
              {researchItem.codeLink && (
                <a href={researchItem.codeLink} target="_blank" rel="noopener noreferrer">
                  View code
                </a>
              )}
              {researchItem.slidesLink && (
                <a href={researchItem.slidesLink} target="_blank" rel="noopener noreferrer">
                  View slides
                </a>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default Research;
